import React, { useMemo, useState } from "react";
import config from "@config";
import apiEndpoints from "@store/apiEndpoints";
import errorFunctions from "@store/errorFunctions";
import { useToast } from "@components/Toast/ToastContext";
import AddressConfirmModal from "@pages/CustomerDetail/Devices/SetWarrantyDetails/components/AddressConfirmModal";
import "./DeliveryAddress.css";

/*
Delivery address held on the ticket itself - this is where the repaired or
replaced item is sent back to, and it can differ from the address on the
customer's account. Editing reuses the same modal as ticket creation.
 */
export default function DeliveryAddress({
  details,
  protectedFetch,
  userInfo,
  retrieveWarranty,
}) {
  const { showToast } = useToast();
  const [showEdit, setShowEdit] = useState(false);
  const [saving, setSaving] = useState(false);

  const safeDetails = details || {};

  // Memoised so the modal does not reset the staff member's draft on every
  // re-render of this page.
  const address = useMemo(
    () => ({
      address_line_one: safeDetails.address_line_one || "",
      address_line_two: safeDetails.address_line_two || "",
      suburb: safeDetails.suburb || "",
      state: safeDetails.state || "",
      postcode: safeDetails.postcode || "",
      country: safeDetails.country || config.country,
    }),
    [
      safeDetails.address_line_one,
      safeDetails.address_line_two,
      safeDetails.suburb,
      safeDetails.state,
      safeDetails.postcode,
      safeDetails.country,
    ],
  );

  const hasAddress = [
    address.address_line_one,
    address.address_line_two,
    address.suburb,
    address.state,
    address.postcode,
  ].some(Boolean);

  const handleSave = (draft) => {
    setSaving(true);
    protectedFetch(
      apiEndpoints.ticket.address(safeDetails.id),
      "PATCH",
      {
        address_line_one: draft.address_line_one || "",
        address_line_two: draft.address_line_two || "",
        suburb: draft.suburb || "",
        state: draft.state || "",
        postcode: draft.postcode || "",
        country: draft.country || config.country,
      },
      errorFunctions.checkResponseThenJson,
    )
      .then(() => {
        showToast("Return address updated", "success");
        setShowEdit(false);
        retrieveWarranty?.();
      })
      .catch((err) => {
        console.log("fail", err);
        showToast(err?.message || "Failed to update return address", "error");
      })
      .finally(() => {
        setSaving(false);
      });
  };

  return (
    <div className="card mb-3" data-testid="delivery-address">
      <div className="card-header delivery-address-header">
        <h6>Return Address</h6>
        <button
          type="button"
          className="k-btn k-btn-primary k-btn-sm"
          onClick={() => setShowEdit(true)}
          data-testid="delivery-address-edit-btn"
        >
          Edit
        </button>
      </div>
      <div className="card-body">
        {hasAddress ? (
          <>
            <div className={"single-warranty-details-item"}>
              <span className={"single-warranty-details-label"}>
                Address Line 1:{" "}
              </span>
              <span>{address.address_line_one || "-"}</span>
            </div>
            <div className={"single-warranty-details-item"}>
              <span className={"single-warranty-details-label"}>
                Address Line 2:{" "}
              </span>
              <span>{address.address_line_two || "-"}</span>
            </div>
            <div className={"single-warranty-details-item"}>
              <span className={"single-warranty-details-label"}>Suburb: </span>
              <span>{address.suburb || "-"}</span>
            </div>
            <div className={"single-warranty-details-item"}>
              <span className={"single-warranty-details-label"}>State: </span>
              <span>{address.state || "-"}</span>
            </div>
            <div className={"single-warranty-details-item"}>
              <span className={"single-warranty-details-label"}>
                Post Code:{" "}
              </span>
              <span>{address.postcode || "-"}</span>
            </div>
          </>
        ) : (
          <div className="delivery-address-empty">
            No return address on this ticket
          </div>
        )}
      </div>

      {/* react-modal portals to <body>, so nesting it here is layout-neutral */}
      <AddressConfirmModal
        isOpen={showEdit}
        mode="edit"
        editOnly={true}
        address={address}
        submitting={saving}
        protectedFetch={protectedFetch}
        userInfo={userInfo}
        onSaveEdit={handleSave}
        onClose={() => !saving && setShowEdit(false)}
      />
    </div>
  );
}
